import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  AdminModal, ConfirmDialog, FormField,
  LoadingState, ErrorState, ApiModeBanner, inputCls,
} from "../components/AdminUI";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import { useAdminData } from "../../../store/adminDataStore";
import type { OrderRescheduleLog } from "../../../store/types";

const NOW = () => new Date().toISOString();
const TODAY = () => new Date().toISOString().slice(0, 10);

function emptyLog(orderId = ""): OrderRescheduleLog {
  return {
    id: `orl-${Date.now()}`,
    order_id: orderId,
    original_shoot_date: TODAY(),
    new_shoot_date: TODAY(),
    requested_at: NOW(),
    approved_at: null,
    reason: "",
    reschedule_count: 1,
    note: "",
  };
}

function EditModal({
  form, onChange, onClose, onSave, saving, isNew, orders,
}: {
  form: OrderRescheduleLog; onChange: (v: OrderRescheduleLog) => void;
  onClose: () => void; onSave: () => void; saving: boolean; isNew: boolean;
  orders: Array<{ id: string; label: string }>;
}) {
  const t = useAdminT();
  const set = (field: keyof OrderRescheduleLog, value: unknown) => onChange({ ...form, [field]: value });
  return (
    <AdminModal
      title={isNew ? t("tabifotoAdmin.order_reschedule_logs.add_log") : t("tabifotoAdmin.order_reschedule_logs.edit_log")}
      onClose={onClose} onSave={onSave} saving={saving}
    >
      <FormField label={t("tabifotoAdmin.order_reschedule_logs.order_id")} required>
        <select className={inputCls} value={form.order_id} onChange={(e) => set("order_id", e.target.value)}>
          <option value="">—</option>
          {orders.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.order_reschedule_logs.original_shoot_date")} required>
          <input className={inputCls} type="date" value={form.original_shoot_date}
            onChange={(e) => set("original_shoot_date", e.target.value)} />
        </FormField>
        <FormField label={t("tabifotoAdmin.order_reschedule_logs.new_shoot_date")} required>
          <input className={inputCls} type="date" value={form.new_shoot_date}
            onChange={(e) => set("new_shoot_date", e.target.value)} />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.order_reschedule_logs.requested_at")}>
          <input className={inputCls} type="datetime-local" value={form.requested_at.slice(0, 16)}
            onChange={(e) => set("requested_at", e.target.value + ":00Z")} />
        </FormField>
        <FormField label={t("tabifotoAdmin.order_reschedule_logs.approved_at")}>
          <input className={inputCls} type="datetime-local"
            value={form.approved_at ? form.approved_at.slice(0, 16) : ""}
            onChange={(e) => set("approved_at", e.target.value ? e.target.value + ":00Z" : null)} />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.order_reschedule_logs.reschedule_count")}>
          <input className={inputCls} type="number" min={1} value={form.reschedule_count}
            onChange={(e) => set("reschedule_count", Number(e.target.value))} />
        </FormField>
      </div>
      <FormField label={t("tabifotoAdmin.order_reschedule_logs.reason")}>
        <textarea className={`${inputCls} h-20 resize-none`} value={form.reason}
          onChange={(e) => set("reason", e.target.value)} maxLength={500} />
      </FormField>
      <FormField label={t("tabifotoAdmin.order_reschedule_logs.note")}>
        <textarea className={`${inputCls} h-16 resize-none`} value={form.note}
          onChange={(e) => set("note", e.target.value)} maxLength={500} />
      </FormField>
    </AdminModal>
  );
}

export default function AdminRescheduleLogsPage() {
  const t = useAdminT();
  const { state, status, isApiMode, upsertRescheduleLog, removeRescheduleLog, refetchRescheduleLogs } = useAdminData();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preFilter = searchParams.get("order") ?? "all";

  const [editItem, setEditItem] = useState<OrderRescheduleLog | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filterOrder, setFilterOrder] = useState<string>(preFilter);
  const [filterApproved, setFilterApproved] = useState<"all" | "pending" | "approved">("all");

  const orders = state.orders.map((o) => ({ id: o.id, label: `${o.order_number} — ${o.customer_name}` }));

  const filtered = [...state.orderRescheduleLogs]
    .filter((l) => {
      if (filterOrder !== "all" && l.order_id !== filterOrder) return false;
      if (filterApproved === "pending" && l.approved_at !== null) return false;
      if (filterApproved === "approved" && l.approved_at === null) return false;
      return true;
    })
    .sort((a, b) => b.requested_at.localeCompare(a.requested_at));

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await upsertRescheduleLog(editItem);
      toast.success(t("tabifotoAdmin.common.save_success"));
      setEditItem(null);
    } catch { toast.error(t("tabifotoAdmin.common.save_error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await removeRescheduleLog(deleteId);
      toast.success(t("tabifotoAdmin.common.delete_success"));
    } catch { toast.error(t("tabifotoAdmin.common.delete_error")); }
    finally { setDeleteId(null); }
  };

  const APPROVAL_TABS = [
    { key: "all" as const, label: t("tabifotoAdmin.order_reschedule_logs.filter_all") },
    { key: "pending" as const, label: t("tabifotoAdmin.order_reschedule_logs.filter_pending") },
    { key: "approved" as const, label: t("tabifotoAdmin.order_reschedule_logs.filter_approved") },
  ];

  return (
    <AdminLayout
      title={t("tabifotoAdmin.order_reschedule_logs.title")}
      subtitle={t("tabifotoAdmin.order_reschedule_logs.subtitle")}
      action={
        <button onClick={() => setEditItem(emptyLog(filterOrder !== "all" ? filterOrder : ""))}
          className="flex items-center gap-2 px-4 py-2 bg-[#2C2C2C] text-white text-sm rounded-md hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
          {t("tabifotoAdmin.order_reschedule_logs.add_log")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          <button onClick={() => setFilterOrder("all")}
            className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterOrder === "all" ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
            {t("tabifotoAdmin.order_reschedule_logs.filter_all")}
          </button>
          {orders.map((o) => (
            <button key={o.id} onClick={() => setFilterOrder(o.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterOrder === o.id ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {o.label.split(" — ")[0]}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          {APPROVAL_TABS.map((tab) => (
            <button key={tab.key} onClick={() => setFilterApproved(tab.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterApproved === tab.key ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {status.rescheduleLogs.loading ? <LoadingState /> :
        status.rescheduleLogs.error ? <ErrorState message={status.rescheduleLogs.error} onRetry={refetchRescheduleLogs} /> : (
          <div className="space-y-2">
            {filtered.length === 0 ? (
              <div className="bg-white rounded-lg border border-[#D9D9D9]/60 py-12 text-center text-[#2C2C2C]/30 text-sm font-light">
                {t("tabifotoAdmin.order_reschedule_logs.no_logs")}
              </div>
            ) : filtered.map((log) => {
              const order = state.orders.find((o) => o.id === log.order_id);
              const isPending = log.approved_at === null;
              return (
                <div key={log.id} className={`bg-white rounded-lg border p-5 ${isPending ? "border-amber-200" : "border-[#D9D9D9]/60"}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <button onClick={() => navigate(`/admin/orders?order=${log.order_id}`)}
                          className="text-xs font-mono text-[#C8B8A6] hover:text-[#2C2C2C] cursor-pointer whitespace-nowrap">
                          {order ? order.order_number : log.order_id}
                        </button>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-light ${isPending ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
                          {isPending ? t("tabifotoAdmin.order_reschedule_logs.pending_badge") : t("tabifotoAdmin.order_reschedule_logs.approved_badge")}
                        </span>
                        <span className="text-xs text-[#2C2C2C]/40">
                          #{log.reschedule_count}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F7F6F3] rounded-md">
                          <span className="text-sm font-light text-[#2C2C2C]/50 line-through">{log.original_shoot_date}</span>
                        </div>
                        <div className="w-5 h-5 flex items-center justify-center text-[#C8B8A6]">
                          <i className="ri-arrow-right-line text-base" />
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#C8B8A6]/10 rounded-md">
                          <span className="text-sm font-light text-[#2C2C2C]">{log.new_shoot_date}</span>
                        </div>
                      </div>
                      {log.reason && (
                        <p className="text-sm text-[#2C2C2C]/60 font-light line-clamp-2">{log.reason}</p>
                      )}
                      <div className="flex gap-4 mt-2 text-xs text-[#2C2C2C]/30">
                        <span>{t("tabifotoAdmin.order_reschedule_logs.requested_at")}: {log.requested_at.slice(0, 10)}</span>
                        {log.approved_at && (
                          <span>{t("tabifotoAdmin.order_reschedule_logs.approved_at")}: {log.approved_at.slice(0, 10)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setEditItem({ ...log })}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer">
                        <i className="ri-edit-line text-base" />
                      </button>
                      <button onClick={() => setDeleteId(log.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {editItem && (
        <EditModal form={editItem} onChange={setEditItem} onClose={() => setEditItem(null)}
          onSave={handleSave} saving={saving}
          isNew={!state.orderRescheduleLogs.some((l) => l.id === editItem.id)}
          orders={orders} />
      )}
      {deleteId && (
        <ConfirmDialog title={t("tabifotoAdmin.order_reschedule_logs.delete_confirm")}
          message={t("tabifotoAdmin.common.cannot_undone")} danger
          onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      )}
    </AdminLayout>
  );
}
